import type { ReactNode } from 'react'
import type { Channel, Idea } from '../api'
import { Link } from '../app/Link'
import { CHANNEL_LABEL, FORMAT_LABEL, STATUS_LABEL } from '../domain/labels'
import { firstLineOf } from '../domain/notes'
import { isPending } from '../state/ideas'
import { ChannelIcon } from './ChannelIcon'
import { setPosted } from './pickers'
import { PostedMark } from './PostedMark'
import { SeriesDot } from './SeriesDot'
import { StatusIcon } from './StatusIcon'
import './idea-row.css'

/** Which channel a list is showing, whose mark would say nothing on every row. */
type ChannelView = Channel | 'all'

/**
 * One idea, one line: status ring, title, channel marks, format, series dot.
 *
 * The ring is its own 44px button, so the status can be changed from the
 * list without opening the idea; the rest of the row is a link to it.
 */
export function IdeaRow({
  idea,
  view = 'all',
  onStatusTap,
}: {
  idea: Idea
  /** The channel the list is showing, whose mark would say nothing on every row. */
  view?: ChannelView
  onStatusTap: (idea: Idea) => void
}) {
  const pending = isPending(idea)
  const lead = (
    <button
      type="button"
      className="idea-row-status"
      aria-label={`${STATUS_LABEL[idea.status]}. Change status`}
      disabled={pending}
      onClick={() => onStatusTap(idea)}
    >
      <StatusIcon status={idea.status} />
    </button>
  )
  return (
    <RowShell idea={idea} lead={lead}>
      <span className="idea-row-title">{idea.title}</span>
      <ChannelMarks idea={idea} view={view} />
      <span className="idea-row-format mono">{FORMAT_LABEL[idea.format]}</span>
      <SeriesDot series={idea.series} />
    </RowShell>
  )
}

/**
 * A LinkedIn topic in the writing list: the topic, and under it the first
 * line of the notes Martin writes it from. No format, since that is `post`
 * on nearly every row; a mark instead when the topic is also filmed.
 *
 * The ring in front is LinkedIn's posting, not the video status: tick it
 * once the post is up, the way a to-do is ticked.
 */
export function WritingRow({ idea }: { idea: Idea }) {
  const note = firstLineOf(idea.body)
  const posted = idea.posted_to.includes('linkedin')
  const lead = (
    <button
      type="button"
      className="idea-row-status"
      aria-label={posted ? 'Posted on LinkedIn. Tap to undo' : 'Mark posted on LinkedIn'}
      aria-pressed={posted}
      disabled={isPending(idea)}
      onClick={() => setPosted(idea, 'linkedin', !posted)}
    >
      <PostedMark posted={posted} />
    </button>
  )
  return (
    <RowShell idea={idea} lead={lead} className="writing-row" hasNote={Boolean(note)}>
      <span className="writing-row-text">
        <span className="idea-row-title">{idea.title}</span>
        {note && <span className="writing-row-note">{note}</span>}
      </span>
      <ChannelMarks idea={idea} view="linkedin" />
      <SeriesDot series={idea.series} />
    </RowShell>
  )
}

/** The row around a lead button and a link to the idea. */
function RowShell({
  idea,
  lead,
  className,
  hasNote,
  children,
}: {
  idea: Idea
  lead: ReactNode
  className?: string
  hasNote?: boolean
  children: ReactNode
}) {
  const pending = isPending(idea)
  return (
    <li
      className={className ? `idea-row ${className}` : 'idea-row'}
      data-pending={pending || undefined}
      data-note={hasNote || undefined}
    >
      {lead}
      {pending ? (
        <span className="idea-row-main">{children}</span>
      ) : (
        <Link to={`/idea/${idea.id}`} className="idea-row-main">
          {children}
        </Link>
      )}
    </li>
  )
}

/**
 * The idea's other channels, as small glyphs. Shorts is what most ideas are,
 * so on the full list only the exceptions are marked; filtered to a channel,
 * every channel but that one is.
 */
function ChannelMarks({ idea, view }: { idea: Idea; view: ChannelView }) {
  const unmarked = view === 'all' ? 'short' : view
  const marks = idea.channels.filter((channel) => channel !== unmarked)
  if (!marks.length) return null
  return (
    <span
      className="idea-row-channels"
      role="img"
      aria-label={`For ${marks.map((channel) => CHANNEL_LABEL[channel]).join(' and ')}`}
    >
      {marks.map((channel) => (
        <ChannelIcon key={channel} channel={channel} size={14} />
      ))}
    </span>
  )
}
